import { createSelector } from '@reduxjs/toolkit';
import { stateKey } from '../../../app/state-key.js';
import { fieldSuggestionsOrderReducer as fieldSuggestionsOrder } from '../../../features/commerce/facets/field-suggestions-order/field-suggestions-order-slice.js';
import { executeSearch } from '../../../features/commerce/search/search-actions.js';
import { loadReducerError } from '../../../utils/errors.js';
import { buildController, } from '../../controller/headless-controller.js';
import { facetResponseSelector, isFacetLoadingResponseSelector, } from '../search/facets/headless-search-facet-options.js';
import { buildCategoryFilterSuggestions, } from './headless-category-filter-suggestions.js';
import { buildFilterSuggestions, } from './headless-filter-suggestions.js';
/**
 * Builds a filter suggestions generator controller for a given commerce engine.
 * @param engine - The commerce engine.
 * @returns The filter suggestions generator controller.
 *
 * @alpha  This controller relies on a Commerce API functionality that is not yet generally available. If you wish to
 * use this feature in an implementation, please contact your Coveo representative.
 *
 * @group Buildable controllers
 * @category FilterSuggestionsGenerator
 */
export function buildFilterSuggestionsGenerator(engine) {
    if (!loadFilterSuggestionsGeneratorReducers(engine)) {
        throw loadReducerError;
    }
    const commonOptions = {
        fetchProductsActionCreator: executeSearch,
        facetResponseSelector,
        isFacetLoadingResponseSelector,
        facetSearch: { type: 'SEARCH' },
    };
    const controller = buildController(engine);
    const createFacetedSearchControllers = createSelector((state) => state.fieldSuggestionsOrder, (facetOrder) => facetOrder.map(({ type, facetId }) => {
        switch (type) {
            case 'hierarchical':
                return buildCategoryFilterSuggestions(engine, {
                    facetId,
                    ...commonOptions,
                });
            default:
                return buildFilterSuggestions(engine, { facetId, ...commonOptions });
        }
    }));
    return {
        ...controller,
        get filterSuggestions() {
            return createFacetedSearchControllers(engine[stateKey]);
        },
        get state() {
            return engine[stateKey].fieldSuggestionsOrder;
        },
    };
}
function loadFilterSuggestionsGeneratorReducers(engine) {
    engine.addReducers({
        fieldSuggestionsOrder,
    });
    return true;
}
